import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Input, useDialog } from '@gokrew/ui';
import { createCrmLead, type CrmLeadRow } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { CrmButton, CrmRequired } from './CrmShared';
import { CrmLocationFields } from './CrmLocationFields';
import styles from './Crm.module.css';

const TEXT_FIELDS = [
  ['firstName', 100],
  ['lastName', 100],
  ['email', 254],
  ['phone', 50],
  ['title', 200],
  ['website', 300],
  ['linkedinUrl', 300],
] as const;

/** A lead typed in by hand (the Leads page also imports them from CSV). */
export const CrmLeadEditor = ({ dialogId }: { dialogId?: string }) => {
  const { t } = useCrmFormat();
  const { closeDialog } = useDialog();
  const client = useQueryClient();
  const [form, setForm] = useState<
    Required<Omit<CrmLeadRow, 'estimatedEmployees'>> & {
      estimatedEmployees: number | null;
    }
  >({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    companyName: '',
    website: '',
    linkedinUrl: '',
    city: '',
    province: '',
    country: 'Canada',
    estimatedEmployees: null,
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await createCrmLead({
        ...form,
        email: form.email.trim() || undefined,
        estimatedEmployees: form.estimatedEmployees,
      });
      await client.invalidateQueries({ queryKey: ['crm', 'leads'] });
      if (dialogId) closeDialog(dialogId);
    } catch (err) {
      setError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.leads.duplicate'
            : 'crm.save_error'
        )
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <fieldset className={styles.grid} disabled={busy}>
        <label className={styles.fullWidth}>
          <span>
            {t('crm.leads.fields.companyName')}
            <CrmRequired />
          </span>
          <Input
            autoFocus
            required
            maxLength={200}
            value={form.companyName}
            onChange={(event) =>
              setForm((old) => ({ ...old, companyName: event.target.value }))
            }
          />
        </label>
        {TEXT_FIELDS.map(([key, max]) => (
          <label key={key}>
            {t(`crm.leads.fields.${key}`)}
            <Input
              maxLength={max}
              type={
                key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'
              }
              value={form[key]}
              onChange={(event) =>
                setForm((old) => ({ ...old, [key]: event.target.value }))
              }
            />
          </label>
        ))}
        <label>
          {t('crm.leads.fields.estimatedEmployees')}
          <Input
            type="number"
            min={0}
            max={10000000}
            step={1}
            value={form.estimatedEmployees ?? ''}
            onChange={(event) =>
              setForm((old) => ({
                ...old,
                estimatedEmployees:
                  event.target.value === '' ? null : Number(event.target.value),
              }))
            }
          />
        </label>
        <CrmLocationFields
          value={form}
          onChange={(next) => setForm((old) => ({ ...old, ...next }))}
        />
      </fieldset>
      <label>
        {t('crm.leads.fields.notes')}
        <textarea
          rows={3}
          maxLength={10000}
          value={form.notes}
          onChange={(event) =>
            setForm((old) => ({ ...old, notes: event.target.value }))
          }
        />
      </label>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <CrmButton
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => dialogId && closeDialog(dialogId)}
        >
          {t('crm.cancel')}
        </CrmButton>
        <CrmButton
          type="submit"
          variant="adminPrimary"
          disabled={busy || !form.companyName.trim()}
        >
          {t(busy ? 'crm.saving' : 'crm.save')}
        </CrmButton>
      </div>
    </form>
  );
};
